import { useCallback, useEffect, useRef, type RefObject } from "react";

/** How close to the end still counts as reading the latest. */
const NEAR_END = 64;

/**
 * Keeps a transcript at its latest while the owner is reading the latest: anything that grows it
 * (a message, a card, an image loading, the keyboard opening) keeps the end in view. Scrolling up
 * lets go; scrolling back down, or `pin()`, holds on again. `marker` sits inside the content; its
 * nearest scrolling ancestor is the scroller, and its parent is what grows.
 */
export function useStickToBottom(marker: RefObject<HTMLElement | null>, active: boolean): { pin: () => void } {
  const stuck = useRef(true);
  const scroller = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const content = marker.current?.parentElement;
    const element = marker.current?.closest<HTMLElement>(".bw-panel-body") ?? null;
    if (!active || !content || !element) return;
    scroller.current = element;
    const toEnd = () => { element.scrollTop = element.scrollHeight; };
    // Opening (or reopening) the conversation starts at the latest.
    stuck.current = true;
    toEnd();
    const onScroll = () => { stuck.current = element.scrollHeight - element.scrollTop - element.clientHeight <= NEAR_END; };
    const observer = new ResizeObserver(() => { if (stuck.current) toEnd(); });
    observer.observe(content);
    observer.observe(element);
    element.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      observer.disconnect();
      element.removeEventListener("scroll", onScroll);
    };
  }, [marker, active]);

  const pin = useCallback(() => {
    stuck.current = true;
    const element = scroller.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, []);
  return { pin };
}
